import posthog from 'posthog-js';

const posthogKey = process.env.VUE_APP_POSTHOG_KEY;
const posthogHost = process.env.VUE_APP_POSTHOG_HOST || 'https://eu.i.posthog.com';

let isInitialized = false;
let lastTrackedScreenKey = '';
const analyticsContext = {
  app_version: process.env.VUE_APP_VERSION,
  organisation_theme: process.env.VUE_APP_ORGANISATION,
};

function isBrowser() {
  return typeof window !== 'undefined';
}

function getBrowserName() {
  if (!isBrowser()) {
    return 'server';
  }

  const userAgent = String(window.navigator.userAgent || '').toLowerCase();
  const isEdge = /edg\//.test(userAgent);
  const isOpera = /opr\//.test(userAgent);
  const isSamsung = /samsungbrowser/.test(userAgent);
  const isFirefox = /firefox|fxios/.test(userAgent);
  const isChrome = /chrome|crios/.test(userAgent) && !isEdge && !isOpera && !isSamsung;
  const isSafari = /safari/.test(userAgent) && !isChrome && !isEdge && !isFirefox && !isOpera && !isSamsung;

  if (isSafari) return 'safari';
  if (isChrome) return 'chrome';
  if (isEdge) return 'edge';
  if (isFirefox) return 'firefox';
  if (isSamsung) return 'samsung';
  if (isOpera) return 'opera';
  return 'other';
}

function getPlatformName() {
  if (!isBrowser()) {
    return 'server';
  }

  const userAgent = String(window.navigator.userAgent || '').toLowerCase();
  const platform = String(window.navigator.platform || '').toLowerCase();
  const maxTouchPoints = Number(window.navigator.maxTouchPoints || 0);
  const isIPadDesktopMode = platform === 'macintel' && maxTouchPoints > 1;

  if (/iphone|ipad|ipod/.test(userAgent) || isIPadDesktopMode) return 'ios';
  if (/android/.test(userAgent)) return 'android';
  if (/mac/.test(platform)) return 'macos';
  if (/win/.test(platform)) return 'windows';
  if (/linux/.test(platform)) return 'linux';
  return 'other';
}

function getUserDisplayName(userAttributes = {}) {
  const givenName = String(userAttributes?.given_name || '').trim();
  const familyName = String(userAttributes?.family_name || '').trim();
  const fullName = [givenName, familyName].filter(Boolean).join(' ').trim();

  return fullName || String(userAttributes?.email || '').trim() || 'unknown-user';
}

function getUserDistinctId(userAttributes = {}) {
  return String(userAttributes?.email || '').trim() || getUserDisplayName(userAttributes);
}

function getOrganisationName(userAttributes = {}, store = null) {
  return String(
    userAttributes?.['custom:organisation'] ||
      store?.organisation ||
      store?.organisational_theme ||
      'fittr'
  ).trim();
}

function getVisitorType() {
  return analyticsContext.signed_in === true ? 'authenticated' : 'anonymous';
}

function getVisitorLabel() {
  if (analyticsContext.signed_in === true && analyticsContext.user_display_name) {
    return analyticsContext.user_display_name;
  }

  return 'anonymous';
}

function getBaseEventProperties() {
  return {
    ...analyticsContext,
    browser: getBrowserName(),
    platform: getPlatformName(),
    visitor_type: getVisitorType(),
    visitor_label: getVisitorLabel(),
    is_pwa: isBrowser()
      ? Boolean(window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone === true)
      : false,
    path: isBrowser() ? window.location.pathname : undefined,
  };
}

export function initAnalytics({ appVersion, organisationTheme } = {}) {
  analyticsContext.app_version = appVersion || analyticsContext.app_version;
  analyticsContext.organisation_theme = organisationTheme || analyticsContext.organisation_theme;

  if (!posthogKey || !isBrowser() || isInitialized) {
    return false;
  }

  posthog.init(posthogKey, {
    api_host: posthogHost,
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    persistence: 'localStorage+cookie',
  });

  isInitialized = true;
  return true;
}

export function setAnalyticsContext(context = {}) {
  Object.entries(context).forEach(([key, value]) => {
    if (value == null || value === '') {
      delete analyticsContext[key];
      return;
    }

    analyticsContext[key] = value;
  });
}

export function identifyAnalyticsUser(userAttributes = {}, store = null) {
  if (!isInitialized) {
    return;
  }

  const organisation = getOrganisationName(userAttributes, store);
  const userDisplayName = getUserDisplayName(userAttributes);
  const distinctId = getUserDistinctId(userAttributes);

  setAnalyticsContext({
    signed_in: true,
    organisation,
    organisation_theme: store?.organisational_theme || analyticsContext.organisation_theme,
    user_display_name: userDisplayName,
  });

  posthog.identify(distinctId, {
    organisation,
    organisation_theme: store?.organisational_theme || analyticsContext.organisation_theme,
    user_display_name: userDisplayName,
    given_name: userAttributes?.given_name || undefined,
    family_name: userAttributes?.family_name || undefined,
    email: userAttributes?.email || undefined,
  });

  if (organisation) {
    posthog.group('organisation', organisation, {
      organisation,
      organisation_theme: store?.organisational_theme || analyticsContext.organisation_theme,
    });
  }
}

export function resetAnalyticsUser() {
  if (!isInitialized) {
    return;
  }

  posthog.reset();
  lastTrackedScreenKey = '';
  delete analyticsContext.user_display_name;
  analyticsContext.signed_in = false;
}

export function trackEvent(eventName, properties = {}) {
  if (!isInitialized || !eventName) {
    return;
  }

  posthog.capture(eventName, {
    ...getBaseEventProperties(),
    ...properties,
  });
}
